import React, { useEffect, useState, useRef } from "react";
import Input from "../Common/CustomInput";
import CustomSelect from "../Common/CustomSelect";
import api from "../../setupMiddleware";
import Loader from "../Loader/Loader";
import useApiLoader from "../../Utils/useApiLoader";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Popup from "../Common/Popup";
import { FaFilePdf, FaFileAlt } from "react-icons/fa";
import PlusIcon from "../../assets/icons/plus";
import {
  createCandidateFormData,
  getCountryList,
  convertOlToUlByDataList,
  formatNumber,
  numberToWords,
} from "../../Utils/getUserUtitls";
import { Link } from "react-router-dom";
import { getUser } from "../../Utils/auth";
import { useLayout } from "./LayoutContext";
import RichInput from "../Common/RichInput";
import { MdOutlineAttachEmail } from "react-icons/md";

const UpdateCandidate = () => {
  const { layoutRef } = useLayout();
  const navigate = useNavigate();
  const loading = useApiLoader();
  const [roleData, setRoleData] = useState([]);
  const [designationData, setDesignationData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [responsibilities, setResponsibilities] = useState("");
  const [noUserOpen, setNoUserOpen] = useState(false);
  const [apiErrorOpen, setApiErrorOpen] = useState(false);
  const [jobDuty, setJobDuty] = useState();
  const { id } = useParams();

  const loggedUser = getUser();

  const isInitial = useRef(true);

  const designationOptions = designationData?.map((des) => ({
    label: des.title,
    value: des.id,
  }));

  const roleOptions = roleData?.map((ro) => ({
    label: ro.name,
    value: ro.id,
  }));

  const departmentOptions = departmentData?.map((dep) => ({
    label: dep.name,
    value: dep.id,
  }));

  const locationOptions = locationData?.map((loc) => ({
    label: loc.name,
    value: loc.id,
  }));

  const statusOptions = [
    {
      label: "In progress",
      value: "In progress",
    },
    {
      label: "Selected",
      value: "Selected",
    },
    {
      label: "Rejected",
      value: "Rejected",
    },
  ];

  const nationalityOptions = getCountryList();

  const [employee, setEmployee] = useState({});

  const getAllData = async () => {
    try {
      api.get("/api/master-data").then((res) => {
        if (res && res.status === 200) {
          const resData = res.data?.data;
          const filteredRoleData = resData?.roles.filter((item) => {
            // Always remove id=1
            if (item.id === 1) return false;

            // If logged-in user is NOT 1, also remove id=2
            if (loggedUser?.id !== 1 && item.id === 2) return false;

            return true; // keep everything else
          });
          setRoleData(filteredRoleData);
          setDesignationData(resData?.designations);
          setDepartmentData(resData?.departments);
        }
      });
    } catch (err) {
      toast.error(err.response.data.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const getResponsibility = async () => {
    try {
      api.get(`/api/role-responsibilities/${employee?.role_id}`).then((res) => {
        if (res && res.status === 200) {
          setResponsibilities(res.data?.responsibilities[0]?.responsibilities);
        }
      });
    } catch (err) {
      toast.error(err.response.data.message, {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const getAllLocation = async () => {
    api.get("/api/locations").then((res) => {
      if (res && res.status === 200) {
        setLocationData(res.data?.data);
      }
    });
  };

  const getUserById = async () => {
    try {
      await api.get(`/api/candidate/${id}`).then((res) => {
        if (res && res.status === 200) {
          const candidateData = {
            ...res.data?.candidate,
            role_id: res.data?.candidate?.role?.id,
            department_id: res.data?.candidate?.department?.id,
            designation_id: res.data?.candidate?.designation?.id,
          };
          setEmployee(candidateData);
          setResponsibilities(candidateData?.responsibilities);
          setJobDuty(candidateData?.data);
          getAllData();
          getAllLocation();
        }
      });
    } catch (error) {
      if (error?.response?.data?.message == "User not found.") {
        setNoUserOpen(true);
      } else {
        setApiErrorOpen(true);
      }
    }
  };

  const moveCandidateToUser = async () => {
    api
      .post(`/api/candidates/move-to-user`, { candidate_id: id })
      .then((res) => {
        if (res && res.status === 200) {
          toast.success(res.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
        }
      })
      .catch((err) => {
        toast.error(err.response.data.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      });
  };

  useEffect(() => {
    getUserById();
  }, []);

  useEffect(() => {
    if (employee?.role_id) {
      if (isInitial.current) {
        // First time data loaded from API — skip
        isInitial.current = false;
        return;
      }
      // Subsequent changes — call function
      getResponsibility();
    }
  }, [employee?.role_id]);

  const validateFields = () => {
    const newErrors = {};
    if (!employee?.job_country)
      newErrors.job_country = ["Job Country is required"];
    if (!employee?.first_name)
      newErrors.first_name = ["First name is required"];
    if (!employee?.last_name) newErrors.last_name = ["Last name is required"];
    if (!employee?.email) newErrors.email = ["Email is required"];
    if (!employee?.phone) newErrors.phone = ["Phone is required"];
    if (!employee?.role_id) newErrors.role_id = ["Role is required"];
    if (!employee?.department_id)
      newErrors.department_id = ["Department is required"];
    if (!employee?.designation_id)
      newErrors.designation_id = ["Designation is required"];
    if (!employee?.job_location)
      newErrors.job_location = ["Job Location is required"];
    if (!employee?.nationality)
      newErrors.nationality = ["Nationality is required"];
    if (!employee?.fixed_allowance)
      newErrors.fixed_allowance = ["Salary is required"];
    if (!responsibilities)
      newErrors.responsibilities = ["Responsibilities are required"];

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetErrors = () => setFieldErrors({});

  const handleUpdateEmployee = async () => {
    resetErrors();

    // run frontend validation
    if (!validateFields()) {
      window.scrollTo(0, 0);
      return; // stop if errors exist
    }

    try {
      const parsedResponsibilities = convertOlToUlByDataList(responsibilities);
      const payload = {
        ...employee,
        responsibilities: parsedResponsibilities,
        data: jobDuty,
      };
      const formData = createCandidateFormData(payload);
      await api.post(`/api/update-candidate/${id}`, formData).then((res) => {
        if (res && res.status === 200) {
          toast.success(res.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          setTimeout(() => navigate("/onboarding"), 1000);
        }
      });
    } catch (err) {
      if (err) {
        toast.error(err.response.data.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
        setFieldErrors(err.response.data.errors); // same shape
        window.scrollTo(0, 0);
      }
    }
  };

  const generateOfferLetter = async () => {
    try {
      await api
        .post(`/api/offer-letters/create`, { candidate_id: id })
        .then((res) => {
          if (res && res.status === 200) {
            toast.success(res.data?.message, {
              position: "top-left",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
              transition: Bounce,
            });
            setEmployee({ ...employee, offer_letter: res.data.offer_letter });
          }
        });
    } catch (err) {
      if (err) {
        toast.error(err.response.data.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      }
    }
  };

  const sendOfferLetter = async () => {
    try {
      await api
        .post(`/api/offer-letters/send/${employee?.offer_letter?.id}`)
        .then((res) => {
          if (res && res.status === 200) {
            toast.success(res.data?.message, {
              position: "top-left",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
              transition: Bounce,
            });
            setEmployee({ ...employee, offer_letter: res.data.offer_letter });
          }
        });
    } catch (err) {
      if (err) {
        toast.error(err.response.data.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      }
    }
  };

  useEffect(() => {
    let selectedDesignation = "";
    let selectedCountry = "";
    if (employee?.designation_id && designationOptions?.length) {
      // Get designation name from array
      selectedDesignation =
        designationOptions.find((opt) => opt.value === employee?.designation_id)
          ?.label || "";
    }

    if (employee?.job_country && locationOptions?.length) {
      // Get designation name from array
      selectedCountry =
        locationOptions.find((opt) => opt.value === employee?.job_country)
          ?.label || "";
    }

    // Update contract HTML
    let htmlString = "";
    if (employee?.job_country === 1) {
      htmlString = `
      <ol>
        <li>
          <b>Work Location:</b> Your primary work location will be based in ${
            employee?.job_location ? employee?.job_location : "{{job_location}}"
          }, ${employee?.job_country ? selectedCountry : "{{job_country}}"}. 
          The Company may transfer you anywhere within ${
            employee?.job_country ? selectedCountry : "{{job_country}}"
          } during the tenure of your service 
          based on our mutual agreement.
        </li>
        
        <li>
          <b>Position:</b> Your duties as a ${
            selectedDesignation ? selectedDesignation : "{{designation_title}}"
          } are set out in an Annex of this Agreement. 
          You are expected to undertake additional duties as may reasonably be required by the Company.
        </li>

        <li>
          <b>Probationary Period:</b> The first 6 months from the Date of first attendance in office 
          is considered a probationary period. During this period, the Company may terminate this Contract 
          by notifying you 14 days before the specified termination date. You may also terminate this Contract 
          by providing 14 days’ notice.
        </li>

        <li>
          <b>Date of joining:</b> Your date of joining will be effective as per ministry of labour approval 
          or respective competent authority.
        </li>

        <li>
          <b>Contract Duration:</b> This Contract is of indefinite duration and is terminable with one month's 
          notice from either side.
        </li>

        <li>
          <b>Your Compensation:</b> Your monthly compensation will be based on sales performance and 
          achievement-based commission. This commission structure may be revised based on market conditions 
          and company revenue scheme. Please refer to attached incentive plan.
        </li>

        <li>
          <b>Working hours:</b> Working hours will be a minimum of 48 hours weekly with one day off. 
          You agree to work additional hours/shifts as required by business needs.
        </li>

        <li>
          <b>Equipment:</b> The Company may provide mobile phone, computer/laptop, SIM, and required software 
          for business use. You must be professionally accessible at all times.
        </li>

        <li>
          <b>Confidentiality:</b> You must not undertake any work that conflicts with company interests. 
          You may not disclose confidential information during or after employment.
        </li>

        <li>
          <b>Annual leave:</b> You are entitled to 24 working days of paid annual leave, excluding 
          government-declared holidays.
        </li>

        <li>
          <b>Non-competition:</b> For 24 months after termination or expiry of the Contract, you must not 
          compete with or work for businesses similar to the Company within ${
            employee?.job_country ? selectedCountry : "{{job_country}}"
          }, unless approved 
          in writing.
        </li>

        <li>
          <b>Transfer of employment:</b> The company may transfer your duties to other associated entities 
          as required.
        </li>

        <li>
          <b>Creativity production:</b> Any creative work (videos, photography, posts, blogs, etc.) produced 
          during employment becomes company property for unrestricted future use.
        </li>
      </ol>`;
    } else if (employee?.job_country === 2) {
      htmlString = `
      <ol>
        <li>
          <b>Work Location:</b> Your primary work location will be based in ${
            employee?.job_location ? employee?.job_location : "{{job_location}}"
          }. The Company may transfer you anywhere within the Country during the tenure of your service based on mutual agreement.
        </li>

        <li>
          <b>Position:</b> Your duties as an ${
            selectedDesignation ? selectedDesignation : "{{designation_title}}"
          } are set out in the attached Annexure. You are expected to undertake additional duties as may reasonably be required by the Company.
        </li>

        <li>
          <b>Probationary Period:</b> The first 6 months from the date of first attendance in office is considered a probationary period. During this period, the Company has the right to terminate this contract by notifying you 14 days before the specified termination date. You may terminate this contract by providing 14 days’ notice to the Company.
        </li>

        <li>
          <b>Date of Joining:</b> Your date of joining will be effective from the first day of attendance in the office.
        </li>

        <li>
          <b>Contract Duration:</b> This contract is of indefinite duration and is terminable with one month’s notice from the Company to you or vice versa.
        </li>

        <li>
          <b>Compensation:</b> Your monthly total salary will be INR ${
            employee?.fixed_allowance
              ? formatNumber(employee?.fixed_allowance) +
                ` (Rupees ${numberToWords(employee?.fixed_allowance)})`
              : "{{salry}}"
          }, including all allowances. Additionally, you will be enrolled in a monthly/quarterly performance-based incentive program. This commission structure will be revised from time to time based on market conditions and the Company’s revenue scheme in association with banks, financial institutions or business partners.
        </li>

        <li>
          <b>Working Hours:</b> Working hours will be a minimum of 48 hours per week, with one day off. You agree to work such hours and/or shifts and any additional hours connected to this position. The Company has the right to assign working hours in accordance with business requirements, as permitted by law.
        </li>

        <li>
          <b>Equipment:</b> The Company may provide you with (a) a mobile phone, (b) a computer or laptop, (c) a company-paid SIM, and (d) software access, to be used solely for business purposes. The type/brand of the equipment will be decided by the Company at its discretion. You acknowledge that you must remain professionally accessible and that your contact details may be shared with colleagues, clients, and professional contacts for business purposes.
        </li>

        <li>
          <b>Confidentiality:</b> You must not undertake work that conflicts with the interests of the Company. You may not disclose confidential information to unauthorized persons within or outside the Company during or after your employment, in accordance with applicable laws in India or any other country.
        </li>

        <li>
          <b>Annual Leave:</b> Your paid annual leave entitlement will be 20 working days.
        </li>

        <li>
          <b>Non-Competition:</b> During your employment and for 12 months immediately following termination (with or without notice), you agree not to compete, participate, be employed, consult for, or affiliate with any business competing with the Company in India or its global operational regions unless pre-approved in writing by the Company.
        </li>

        <li>
          <b>Transfer of Employment:</b> The Company may require you to transfer your duties to its other associated businesses or entities in the future as needed.
        </li>

        <li>
          <b>Creative Production:</b> Any video shoot, product photography, event data, social media post, news article, blog, or other creative production created by you during employment for business purposes will be the property of the Company and may be used for future purposes without any claim or restriction.
        </li>
      </ol>`;
    }
    setJobDuty(htmlString);
  }, [
    employee?.job_location,
    employee?.job_country,
    employee?.designation_id,
    employee?.fixed_allowance,
    designationData,
  ]);

  useEffect(() => {
    if (fieldErrors && layoutRef.current) {
      layoutRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [fieldErrors]);

  const getFileIconOrPreview = (filePath) => {
    if (!filePath) return null;

    // extract extension safely
    const ext = filePath
      .split("?")[0]
      .split("#")[0]
      .split(".")
      .pop()
      .toLowerCase();

    // if it's an image → return preview
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      return (
        <img
          src={filePath}
          alt="Preview"
          className="w-16 h-16 object-cover rounded" // adjust size/styles
        />
      );
    }

    // pdf
    if (ext === "pdf") return <FaFilePdf className="text-[#c30f0a] text-7xl" />;

    // default icon
    return <FaFileAlt className="text-[#c30f0a] text-lg" />;
  };

  return (
    <>
      {loading && <Loader opacity={90} fullscreen />}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full mx-auto px-4 py-3 gap-4">
        {/* Left Content */}
        <p>
          <span className="block text-xl sm:text-2xl font-medium text-[#de2b26]">
            Edit Candidate
          </span>
          <span className="block text-sm text-[#142b70]/50">
            Update candidate details and status
          </span>
        </p>

        {/* Right Content */}
        <div className="flex">
          {/* {!employee?.offer_letter && ( */}
          <button
            onClick={generateOfferLetter}
            className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
          >
            <PlusIcon iconClass="w-4 h-4" color="#ffffff" />
            Generate Offer Letter
          </button>
          {/* )} */}
          {/* //  (
          //   <button
          //     onClick={sendOfferLetter}
          //     className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
          //   >
          //     <MdOutlineAttachEmail size={20} color="#ffffff" />
          //     Send Offer Letter
          //   </button>
          // )} */}
        </div>
      </div>
      <div className="w-full mx-auto px-4 py-3 relative">
        <div className="w-full mx-auto p-8 relative rounded-3xl bg-white border border-[#a48572]/10">
          <div className="flex flex-col lg:flex-row flex-grow justify-start items-start gap-[60px]">
            {/* Form Col 1*/}
            <div className="flex flex-col flex-grow gap-6 w-full">
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Resume</p>
                  <div className="relative">
                    <Link
                      to={employee?.resume_path}
                      target="_blank"
                      className="flex flex-col items-center justify-center rounded-2xl shadow-md p-4 bg-[#de2b26]/10 mb-3"
                    >
                      {getFileIconOrPreview(employee?.resume_path)}
                      <p className="text-sm text-[#142b70]/80 mt-2">
                        View Resume
                      </p>
                    </Link>
                  </div>
                  {/* <div className="relative">
                    <FileUpload
                      label="Resume"
                      accept=".jpeg,.jpg,.png,.pdf"
                      multiple={false}
                      onChange={(file) => {
                        setEmployee({ ...employee, resume: file });
                        if (fieldErrors.resume) {
                          setFieldErrors({
                            ...fieldErrors,
                            resume: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.resume)}
                      helperText={fieldErrors?.resume?.[0]}
                    />
                  </div> */}
                </div>
              </div>
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Job Location</p>
                  <Input
                    type="text"
                    placeholder="Enter Job Location"
                    value={employee?.job_location}
                    onChange={(e) => {
                      setEmployee({
                        ...employee,
                        job_location: e.target.value,
                      });
                      if (fieldErrors.job_location) {
                        setFieldErrors({
                          ...fieldErrors,
                          job_location: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.job_location)}
                    helperText={fieldErrors?.job_location?.[0]}
                  />
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Job Country</p>
                  <CustomSelect
                    options={locationOptions}
                    value={employee?.job_country}
                    placeholder="Job Location"
                    width="w-full"
                    height="h-[46px]"
                    fontSize="14px"
                    onChange={(op) => {
                      setEmployee({
                        ...employee,
                        job_country: op.value,
                      });
                      if (fieldErrors.job_country) {
                        setFieldErrors({
                          ...fieldErrors,
                          job_country: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.job_country)}
                    helperText={fieldErrors?.job_country?.[0]}
                  />
                </div>
              </div>
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">First Name</p>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter First name"
                      value={employee?.first_name}
                      onChange={(e) => {
                        setEmployee({
                          ...employee,
                          first_name: e.target.value,
                        });
                        if (fieldErrors.first_name) {
                          setFieldErrors({
                            ...fieldErrors,
                            first_name: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.first_name)}
                      helperText={fieldErrors?.first_name?.[0]}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Middle Name</p>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter Middle name"
                      value={employee?.middle_name}
                      onChange={(e) =>
                        setEmployee({
                          ...employee,
                          middle_name: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Last Name</p>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter Last name"
                      value={employee?.last_name}
                      onChange={(e) => {
                        setEmployee({
                          ...employee,
                          last_name: e.target.value,
                        });
                        if (fieldErrors.last_name) {
                          setFieldErrors({
                            ...fieldErrors,
                            last_name: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.last_name)}
                      helperText={fieldErrors?.last_name?.[0]}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Email</p>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter Email"
                      value={employee?.email}
                      onChange={(e) => {
                        setEmployee({
                          ...employee,
                          email: e.target.value,
                        });
                        if (fieldErrors.email) {
                          setFieldErrors({
                            ...fieldErrors,
                            email: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.email)}
                      helperText={fieldErrors?.email?.[0]}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Phone Number</p>
                  <div className="relative">
                    <Input
                      type="number"
                      placeholder="Enter Phone Number"
                      value={employee?.phone}
                      onChange={(e) => {
                        setEmployee({
                          ...employee,
                          phone: `${e.target.value}`,
                        });
                        if (fieldErrors.phone) {
                          setFieldErrors({
                            ...fieldErrors,
                            phone: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.phone)}
                      helperText={fieldErrors?.phone?.[0]}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-5">
                {employee?.job_country === 1 ? (
                  <div className="flex flex-col gap-2 w-full">
                    <p className="text-sm text-[#142b70]/80">Passport Number</p>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Enter Passport Number"
                        value={employee?.passport_number}
                        onChange={(e) => {
                          setEmployee({
                            ...employee,
                            passport_number: `${e.target.value}`,
                          });
                          if (fieldErrors.passport_number) {
                            setFieldErrors({
                              ...fieldErrors,
                              passport_number: undefined,
                            });
                          }
                        }}
                        error={Boolean(fieldErrors.passport_number)}
                        helperText={fieldErrors?.passport_number?.[0]}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 w-full">
                    <p className="text-sm text-[#142b70]/80">Adhaar Number</p>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Enter Adhaar Number"
                        value={employee?.adharcard_no}
                        onChange={(e) => {
                          setEmployee({
                            ...employee,
                            adharcard_no: `${e.target.value}`,
                          });
                          if (fieldErrors.adharcard_no) {
                            setFieldErrors({
                              ...fieldErrors,
                              adharcard_no: undefined,
                            });
                          }
                        }}
                        error={Boolean(fieldErrors.adharcard_no)}
                        helperText={fieldErrors?.adharcard_no?.[0]}
                      />
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Date of Joining</p>
                  <div className="relative">
                    <Input
                      type="date"
                      placeholder="Enter Date of Joining"
                      value={employee?.joining_date}
                      onChange={(e) => {
                        setEmployee({
                          ...employee,
                          joining_date: e.target.value,
                        });
                        if (fieldErrors.joining_date) {
                          setFieldErrors({
                            ...fieldErrors,
                            joining_date: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.joining_date)}
                      helperText={fieldErrors?.joining_date?.[0]}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Nationality</p>
                  <CustomSelect
                    options={nationalityOptions}
                    value={employee?.nationality}
                    placeholder="Nationality"
                    width="w-full"
                    height="h-[46px]"
                    fontSize="14px"
                    searchable
                    onChange={(op) => {
                      setEmployee({
                        ...employee,
                        nationality: op.value,
                      });
                      if (fieldErrors.nationality) {
                        setFieldErrors({
                          ...fieldErrors,
                          nationality: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.nationality)}
                    helperText={fieldErrors?.nationality?.[0]}
                  />
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Department</p>
                  <CustomSelect
                    options={departmentOptions}
                    value={employee?.department_id}
                    placeholder="Department"
                    width="w-full"
                    height="h-[46px]"
                    fontSize="14px"
                    onChange={(op) => {
                      setEmployee({
                        ...employee,
                        department_id: op.value,
                      });
                      if (fieldErrors.department_id) {
                        setFieldErrors({
                          ...fieldErrors,
                          department_id: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.department_id)}
                    helperText={fieldErrors?.department_id?.[0]}
                  />
                </div>
              </div>
            </div>

            {/* Form Col 2 */}
            <div className="flex flex-col flex-grow gap-6 w-full">
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Role</p>
                  <CustomSelect
                    options={roleOptions}
                    value={employee?.role_id}
                    placeholder="Role"
                    width="w-full"
                    height="h-[46px]"
                    fontSize="14px"
                    onChange={(op) => {
                      setEmployee({
                        ...employee,
                        role_id: op.value,
                        tl_id: 0,
                        hr_head: null,
                        manager: null,
                      });
                      if (fieldErrors.role_id) {
                        setFieldErrors({
                          ...fieldErrors,
                          role_id: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.role_id)}
                    helperText={fieldErrors?.role_id?.[0]}
                  />
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Designation</p>
                  <CustomSelect
                    options={designationOptions}
                    value={employee?.designation_id}
                    placeholder="Designation"
                    width="w-full"
                    height="h-[46px]"
                    fontSize="14px"
                    onChange={(op) => {
                      setEmployee({
                        ...employee,
                        designation_id: op.value,
                      });
                      if (fieldErrors.designation_id) {
                        setFieldErrors({
                          ...fieldErrors,
                          designation_id: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.designation_id)}
                    helperText={fieldErrors?.designation_id?.[0]}
                  />
                </div>
              </div>
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Reporting Line</p>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter Reporting Line"
                      value={employee?.reporting_line}
                      onChange={(e) => {
                        setEmployee({
                          ...employee,
                          reporting_line: `${e.target.value}`,
                        });
                        if (fieldErrors.reporting_line) {
                          setFieldErrors({
                            ...fieldErrors,
                            reporting_line: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.reporting_line)}
                      helperText={fieldErrors?.reporting_line?.[0]}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">Salary</p>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Enter Salary amount"
                      value={employee?.fixed_allowance}
                      onChange={(e) => {
                        setEmployee({
                          ...employee,
                          fixed_allowance: `${e.target.value}`,
                        });
                        if (fieldErrors.fixed_allowance) {
                          setFieldErrors({
                            ...fieldErrors,
                            fixed_allowance: undefined,
                          });
                        }
                      }}
                      error={Boolean(fieldErrors.fixed_allowance)}
                      helperText={fieldErrors?.fixed_allowance?.[0]}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80">
                    Role Responsibilities
                  </p>
                  <RichInput
                    value={responsibilities}
                    onChange={setResponsibilities}
                    placeholder="Write description here..."
                    error={Boolean(fieldErrors.responsibilities)}
                    helperText={fieldErrors?.responsibilities?.[0]}
                    disable={!responsibilities}
                  />
                </div>
              </div>
              <div className="flex justify-between gap-5">
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-[#142b70]/80 font-bold">Status</p>
                  <CustomSelect
                    options={statusOptions}
                    value={employee?.status}
                    placeholder="Status"
                    width="w-full"
                    height="h-[46px]"
                    fontSize="14px"
                    onChange={(op) => {
                      setEmployee({
                        ...employee,
                        status: op.value,
                      });
                      if (fieldErrors.status) {
                        setFieldErrors({
                          ...fieldErrors,
                          status: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.status)}
                    helperText={fieldErrors?.status?.[0]}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between gap-5 mt-6">
            <div className="flex flex-col gap-2 w-full">
              <p className="text-sm text-[#142b70]/80">
                Job Conditions / Duties
              </p>
              <RichInput
                value={jobDuty}
                onChange={setJobDuty}
                placeholder="Write conditions here..."
                error={Boolean(fieldErrors.jobDuty)}
                helperText={fieldErrors?.jobDuty?.[0]}
                disable={!jobDuty}
              />
            </div>
          </div>
          {/* Footer */}
          <div className="flex justify-end gap-4 mt-6">
            <button
              className="px-4 py-2 rounded-md bg-[#de2b26] text-white font-medium"
              onClick={moveCandidateToUser}
            >
              Move to Employee List
            </button>
            <button
              className="px-4 py-2 rounded-md bg-[#de2b26] text-white font-medium"
              onClick={handleUpdateEmployee}
            >
              Update
            </button>
          </div>
        </div>
      </div>
      <Popup
        isOpen={noUserOpen}
        onClose={() => (window.location = "/employees")}
        title="Error"
      >
        <div className="flex flex-col gap-6 mt-[30px] mb-[30px]">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-[#142b70]/80 text-left">
              User Not Found!
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={() => (window.location = "/onboarding")}
            className="px-10 py-3.5 rounded-md bg-[#de2b26] text-white font-medium disabled:opacity-50"
          >
            Ok
          </button>
        </div>
      </Popup>
      <Popup
        isOpen={apiErrorOpen}
        onClose={() => (window.location = "/onboarding")}
        title="Error"
      >
        <div className="flex flex-col gap-6 mt-[30px] mb-[30px]">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-[#142b70]/80 text-left">
              Something went wrong! Please try again later.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={() => (window.location = "/employees")}
            className="px-10 py-3.5 rounded-md bg-[#de2b26] text-white font-medium disabled:opacity-50"
          >
            Ok
          </button>
        </div>
      </Popup>
      <ToastContainer />
    </>
  );
};

export default UpdateCandidate;
